import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import { PILLARS } from '../config/pillars';

const StackFlowContext = createContext(null);

// ─── Seed data mappers ───
// Pure functions mapping accumulated panelData to initial form values for a target panel.

const COUNTRY_CCY_MAP = {
  Kenya: 'KES',
  India: 'INR',
  Brazil: 'BRL',
  Mexico: 'MXN',
  Turkey: 'TRY',
  Bangladesh: 'BDT',
  'South Africa': 'ZAR',
  Egypt: 'EGP',
  Ethiopia: 'ETB',
};

const SEED_MAPPERS = {
  exchange_rates: (panelData) => {
    const yd = panelData.yield_discovery;
    if (!yd) return null;
    return {
      sourceCcy: yd.source || null,
      sinkCcy: COUNTRY_CCY_MAP[yd.sink] || null,
      amount: yd.amount || null,
    };
  },
  compliance: (panelData) => {
    const yd = panelData.yield_discovery;
    if (!yd) return null;
    return {
      contextHint: `${yd.source} to ${yd.sink}, ${yd.instrument}`,
    };
  },
  hedging: (panelData) => {
    const yd = panelData.yield_discovery;
    if (!yd) return null;
    const tenorMatch = yd.tenor ? yd.tenor.match(/(\d+)/) : null;
    return {
      capitalCcy: yd.source || null,
      amount: yd.amount || null,
      tenor: tenorMatch ? parseInt(tenorMatch[1], 10) : null,
    };
  },
  liquidity: (panelData) => {
    const hd = panelData.hedging;
    if (!hd || !hd.selectedInstrument) return null;
    return {
      instrument: hd.selectedInstrument,
    };
  },
  execution: () => null,
  portfolio: () => null,
};

// ─── Reducer ───

const initialState = {
  completed: [],       // array of pillar keys (serializable, converted to Set in helpers)
  panelData: {},
  flowStartedAt: null,
  currentPillar: 'yield_discovery',
};

function flowReducer(state, action) {
  switch (action.type) {
    case 'COMMIT_PANEL': {
      const { key, data } = action.payload;
      const completed = state.completed.includes(key)
        ? state.completed
        : [...state.completed, key];
      return {
        ...state,
        completed,
        panelData: {
          ...state.panelData,
          [key]: { ...state.panelData[key], ...data },
        },
        flowStartedAt: state.flowStartedAt || new Date().toISOString(),
      };
    }
    case 'SET_CURRENT_PILLAR':
      return { ...state, currentPillar: action.payload };
    case 'RESET_FLOW':
      return { ...initialState };
    default:
      return state;
  }
}

// ─── Provider ───

export const StackFlowProvider = ({ children }) => {
  const [state, dispatch] = useReducer(flowReducer, initialState);

  const completedSet = useMemo(() => new Set(state.completed), [state.completed]);

  const commitPanel = useCallback((key, data) => {
    dispatch({ type: 'COMMIT_PANEL', payload: { key, data } });
  }, []);

  const getSeedData = useCallback((key) => {
    const mapper = SEED_MAPPERS[key];
    if (!mapper) return null;
    return mapper(state.panelData);
  }, [state.panelData]);

  const hasSeedData = useCallback((key) => {
    return getSeedData(key) !== null;
  }, [getSeedData]);

  const getNextPillar = useCallback((afterKey) => {
    const refKey = afterKey || state.currentPillar;
    const idx = PILLARS.findIndex(p => p.key === refKey);
    if (idx === -1) return null;
    for (let i = idx + 1; i < PILLARS.length; i++) {
      if (!completedSet.has(PILLARS[i].key)) {
        return PILLARS[i];
      }
    }
    return null;
  }, [state.currentPillar, completedSet]);

  const getCompletedPillars = useCallback(() => {
    return PILLARS.filter(p => completedSet.has(p.key)).map(p => p.key);
  }, [completedSet]);

  const resetFlow = useCallback(() => {
    dispatch({ type: 'RESET_FLOW' });
  }, []);

  const setCurrentPillar = useCallback((key) => {
    dispatch({ type: 'SET_CURRENT_PILLAR', payload: key });
  }, []);

  const value = useMemo(() => ({
    completed: completedSet,
    panelData: state.panelData,
    flowStartedAt: state.flowStartedAt,
    currentPillar: state.currentPillar,
    commitPanel,
    getSeedData,
    hasSeedData,
    getNextPillar,
    getCompletedPillars,
    resetFlow,
    setCurrentPillar,
  }), [
    completedSet, state.panelData, state.flowStartedAt, state.currentPillar,
    commitPanel, getSeedData, hasSeedData, getNextPillar, getCompletedPillars,
    resetFlow, setCurrentPillar,
  ]);

  return (
    <StackFlowContext.Provider value={value}>
      {children}
    </StackFlowContext.Provider>
  );
};

export const useStackFlow = () => {
  const ctx = useContext(StackFlowContext);
  if (!ctx) throw new Error('useStackFlow must be used within StackFlowProvider');
  return ctx;
};

/** Safe version that returns null when outside StackFlowProvider */
export const useStackFlowOptional = () => {
  return useContext(StackFlowContext);
};

export default StackFlowContext;
